import API from "../../../core/api/typed/api";
import {
  HeaderElement,
  HeaderImage,
  MediaDisplay,
  NamesDisplay,
  NavigationStyle,
  Site,
  WeddingSite,
} from "../../../resources/modelTypes";

const HEADER_IMAGES_PATH = "header-images";

type SlotPhotoPks = Map<number, number>;

/**
 * Persists a new site header element order on siteDetails, analogous to
 * reorderPages for Redux page resources. Sites use the Site model context
 * update path (PATCH /sites/:id) rather than a /sort/ endpoint.
 */
export const reorderHeaderElements = (
  siteId: number,
  headerElementOrder: HeaderElement[],
  update: (id: number, data: Partial<Site>) => Promise<Site>,
): Promise<Site> =>
  update(siteId, {
    siteDetails: {
      headerElementOrder,
    },
  } as Partial<WeddingSite>);

/**
 * Persists the couple-photo treatment on siteDetails.mediaDisplay.
 */
export const updateMediaDisplay = (
  siteId: number,
  mediaDisplay: MediaDisplay,
  update: (id: number, data: Partial<Site>) => Promise<Site>,
): Promise<Site> =>
  update(siteId, {
    siteDetails: {
      mediaDisplay,
    },
  } as Partial<WeddingSite>);

/**
 * Persists the names treatment on siteDetails.headerDisplay.
 */
export const updateHeaderDisplay = (
  siteId: number,
  headerDisplay: NamesDisplay,
  update: (id: number, data: Partial<Site>) => Promise<Site>,
): Promise<Site> =>
  update(siteId, {
    siteDetails: {
      headerDisplay,
    },
  } as Partial<WeddingSite>);

/**
 * Persists the navigation bar treatment on siteDetails.navigationStyle.
 */
export const updateNavigationStyle = (
  siteId: number,
  navigationStyle: NavigationStyle,
  update: (id: number, data: Partial<Site>) => Promise<Site>,
): Promise<Site> =>
  update(siteId, {
    siteDetails: {
      navigationStyle,
    },
  } as Partial<WeddingSite>);

/**
 * Sets the site's header images to an ordered photo list in one request.
 * Creates, removes, and reorders atomically; cropped overrides on kept photos
 * are preserved server-side.
 */
export const syncHeaderImages = (
  orderedPhotoPks: number[],
): Promise<HeaderImage[]> =>
  API.put(
    `${HEADER_IMAGES_PATH}/sync/`,
    {},
    orderedPhotoPks.map((photoPk) => ({ photo: photoPk })),
  );

/**
 * Creates a WebsiteHeaderImage for a photo already on the site.
 * POST body uses photo pk; the response is the read serializer payload
 * (resolved original/preview urls) so callers can update local state
 * without a site refetch.
 */
export const createHeaderImage = (
  photoPk: number,
  position: number,
): Promise<HeaderImage> =>
  API.post(HEADER_IMAGES_PATH, {}, { photo: photoPk, position });

/**
 * Removes a photo from the site's modular header.
 */
export const deleteHeaderImage = (headerImageId: number): Promise<void> =>
  API.delete(`${HEADER_IMAGES_PATH}/${headerImageId}`);

/**
 * Reorders header images via PUT header-images/sort/.
 */
export const sortHeaderImages = (
  orderedItems: { id: number; position: number }[],
): Promise<{ id: number; position: number }[]> =>
  API.put(`${HEADER_IMAGES_PATH}/sort/`, {}, orderedItems);

/**
 * Sets or clears the cropped override for a header image placement.
 * Returns the read serializer payload (resolved original/preview).
 */
export const updateHeaderImageCrop = (
  headerImageId: number,
  image: string | null,
): Promise<HeaderImage> =>
  API.patch(`${HEADER_IMAGES_PATH}/${headerImageId}`, {}, { image });

const sortHeaderImagesByPosition = (
  headerImages: HeaderImage[],
): HeaderImage[] =>
  [...headerImages].sort((left, right) => left.position - right.position);

const buildSlotPhotoPks = (
  headerImages: HeaderImage[],
  photoPkByHeaderPhotoUuid: (photoUuid: string) => number | undefined,
): SlotPhotoPks => {
  const slots = new Map<number, number>();

  for (const headerImage of sortHeaderImagesByPosition(headerImages)) {
    const photoPk = photoPkByHeaderPhotoUuid(headerImage.photo);

    if (photoPk == null) {
      throw new Error(
        "Cannot save header slot: existing header photos are not loaded in the photo library.",
      );
    }

    slots.set(headerImage.position, photoPk);
  }

  return slots;
};

/**
 * Places `photoPk` at `slotIndex`, swapping with any photo already in that slot.
 * Slots are keyed by header position so sparse placements (e.g. only slot 1) are preserved.
 */
export const slotPhotoPksWithPlacement = (
  slots: SlotPhotoPks,
  slotIndex: number,
  photoPk: number,
): SlotPhotoPks => {
  const result = new Map(slots);
  let existingSlot: number | undefined;

  for (const [slot, pk] of result) {
    if (pk === photoPk) {
      existingSlot = slot;
      break;
    }
  }

  if (existingSlot === slotIndex) {
    return result;
  }

  if (existingSlot !== undefined) {
    const displaced = result.get(slotIndex);

    if (displaced !== undefined) {
      result.set(existingSlot, displaced);
    } else {
      result.delete(existingSlot);
    }

    result.set(slotIndex, photoPk);
    return result;
  }

  result.set(slotIndex, photoPk);
  return result;
};

const isContiguousFromZero = (slots: SlotPhotoPks): boolean => {
  if (slots.size === 0) {
    return true;
  }

  const maxSlot = Math.max(...slots.keys());

  for (let slot = 0; slot <= maxSlot; slot++) {
    if (!slots.has(slot)) {
      return false;
    }
  }

  return true;
};

const densePhotoPksFromSlots = (slots: SlotPhotoPks): number[] => {
  const maxSlot = Math.max(...slots.keys());

  return Array.from(
    { length: maxSlot + 1 },
    (unused, slot) => slots.get(slot)!,
  );
};

/**
 * Builds the ordered photo-pk list after placing `photoPk` at `slotIndex`.
 * If that photo is already in another slot, the two slots swap so both stay filled.
 * Assumes dense slots starting at 0 (for syncHeaderImages callers).
 */
export const photoPksWithSlot = (
  currentPhotoPks: number[],
  slotIndex: number,
  photoPk: number,
): number[] => {
  const slots = new Map<number, number>();

  currentPhotoPks.forEach((pk, index) => {
    slots.set(index, pk);
  });

  const nextSlots = slotPhotoPksWithPlacement(slots, slotIndex, photoPk);

  if (!isContiguousFromZero(nextSlots)) {
    throw new Error(
      "Cannot place photo in header slot without filling earlier slots.",
    );
  }

  return densePhotoPksFromSlots(nextSlots);
};

const applyHeaderImageCrop = async (
  headerImageId: number,
  croppedImage: string,
  headerImages: HeaderImage[],
): Promise<HeaderImage[]> => {
  const updated = await updateHeaderImageCrop(headerImageId, croppedImage);

  return sortHeaderImagesByPosition(
    headerImages.map((headerImage) =>
      headerImage.id === updated.id ? updated : headerImage,
    ),
  );
};

const saveSparseHeaderImageSlot = async (
  currentHeaderImages: HeaderImage[],
  nextSlots: SlotPhotoPks,
  slotIndex: number,
  photoPk: number,
  croppedImage: string,
  photoPkByHeaderPhotoUuid: (photoUuid: string) => number | undefined,
): Promise<HeaderImage[]> => {
  const ordered = sortHeaderImagesByPosition(currentHeaderImages);
  const headerImageByPhotoPk = new Map<number, HeaderImage>();

  for (const headerImage of ordered) {
    const currentPhotoPk = photoPkByHeaderPhotoUuid(headerImage.photo);

    if (currentPhotoPk != null) {
      headerImageByPhotoPk.set(currentPhotoPk, headerImage);
    }
  }

  const nextPhotoPks = new Set(nextSlots.values());

  for (const headerImage of ordered) {
    const currentPhotoPk = photoPkByHeaderPhotoUuid(headerImage.photo);

    if (currentPhotoPk != null && !nextPhotoPks.has(currentPhotoPk)) {
      await deleteHeaderImage(headerImage.id);
      headerImageByPhotoPk.delete(currentPhotoPk);
    }
  }

  let working = ordered.filter((headerImage) => {
    const currentPhotoPk = photoPkByHeaderPhotoUuid(headerImage.photo);

    return currentPhotoPk != null && nextPhotoPks.has(currentPhotoPk);
  });

  for (const [position, nextPhotoPk] of nextSlots) {
    if (!headerImageByPhotoPk.has(nextPhotoPk)) {
      const created = await createHeaderImage(nextPhotoPk, position);
      headerImageByPhotoPk.set(nextPhotoPk, created);
      working = [...working, created];
    }
  }

  const sortPayload = [...nextSlots.entries()].map(
    ([position, nextPhotoPk]) => ({
      id: headerImageByPhotoPk.get(nextPhotoPk)!.id,
      position,
    }),
  );

  const positionsChanged = sortPayload.some(({ id, position }) => {
    const headerImage = working.find((image) => image.id === id);

    return headerImage?.position !== position;
  });

  if (positionsChanged) {
    await sortHeaderImages(sortPayload);
    working = working.map((headerImage) => {
      const nextPosition = sortPayload.find(
        (item) => item.id === headerImage.id,
      )?.position;

      return nextPosition === undefined
        ? headerImage
        : { ...headerImage, position: nextPosition };
    });
  }

  const target = working.find((headerImage) => {
    const currentPhotoPk = photoPkByHeaderPhotoUuid(headerImage.photo);

    return currentPhotoPk === photoPk && headerImage.position === slotIndex;
  });

  if (!target) {
    return sortHeaderImagesByPosition(working);
  }

  return applyHeaderImageCrop(target.id, croppedImage, working);
};

/**
 * Puts a library photo in a modular header slot and applies its crop.
 * Returns the updated headerImages list for local site state.
 */
export const saveHeaderImageSlot = async (
  currentHeaderImages: HeaderImage[],
  slotIndex: number,
  photoPk: number,
  croppedImage: string,
  photoPkByHeaderPhotoUuid: (photoUuid: string) => number | undefined,
): Promise<HeaderImage[]> => {
  const ordered = sortHeaderImagesByPosition(currentHeaderImages);
  const currentSlots = buildSlotPhotoPks(ordered, photoPkByHeaderPhotoUuid);
  const nextSlots = slotPhotoPksWithPlacement(currentSlots, slotIndex, photoPk);

  if (!isContiguousFromZero(nextSlots)) {
    return saveSparseHeaderImageSlot(
      ordered,
      nextSlots,
      slotIndex,
      photoPk,
      croppedImage,
      photoPkByHeaderPhotoUuid,
    );
  }

  const synced = await syncHeaderImages(densePhotoPksFromSlots(nextSlots));
  const target = synced.find(
    (headerImage) => headerImage.position === slotIndex,
  );

  if (!target) {
    return sortHeaderImagesByPosition(synced);
  }

  return applyHeaderImageCrop(target.id, croppedImage, synced);
};

/**
 * Failure-only reconciliation after a header-image mutation failure.
 * Success paths should apply write responses to local state instead.
 */
export const refetchSite = (siteId: number): Promise<Site> =>
  API.get<Site>(`sites/${siteId}`);
